import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Plus,
  Ticket,
  Users,
  LogOut,
  Menu,
  Zap,
  Building2
} from 'lucide-react';
import { Button } from './ui/button';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';
import { Separator } from './ui/separator';

const navItems = [
  { path: '/', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/create', label: 'Create Coupon', icon: Plus },
  { path: '/coupons', label: 'Coupons', icon: Ticket },
  { path: '/subscribers', label: 'Subscribers', icon: Users },
  { path: '/sponsors', label: 'Sponsors', icon: Building2 },
];

export default function Layout({ children, onLogout }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const NavContent = ({ onItemClick }) => (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className="p-5 pb-4">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-lg bg-primary/20 flex items-center justify-center">
            <Zap className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h1 className="text-base font-semibold text-foreground tracking-tight" style={{ fontFamily: 'Space Grotesk' }}>
              KingTips
            </h1>
            <p className="text-[10px] text-muted-foreground -mt-0.5">Coupon Management</p>
          </div>
        </div>
      </div>

      <Separator className="opacity-50" />

      {/* Nav Items */}
      <nav className="flex-1 p-3 space-y-1">
        {navItems.map((item) => {
          const isActive = location.pathname === item.path;
          const Icon = item.icon;
          return (
            <button
              key={item.path}
              data-testid={`sidebar-nav-${item.path === '/' ? 'dashboard' : item.path.slice(1)}`}
              onClick={() => {
                navigate(item.path);
                onItemClick?.();
              }}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors duration-150 ${
                isActive
                  ? 'bg-primary/15 text-primary border-l-2 border-primary'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
              }`}
            >
              <Icon className="w-4 h-4" />
              {item.label}
            </button>
          );
        })}
      </nav>

      {/* Logout */}
      <div className="p-3 mt-auto">
        <Separator className="mb-3 opacity-50" />
        <Button
          variant="ghost"
          data-testid="sidebar-logout"
          onClick={onLogout}
          className="w-full justify-start gap-3 text-muted-foreground hover:text-destructive"
        >
          <LogOut className="w-4 h-4" />
          Logout
        </Button>
      </div>
    </div>
  );

  return (
    <div className="app-layout">
      {/* Desktop Sidebar */}
      <aside
        data-testid="sidebar"
        className="hidden md:flex w-60 border-r border-border/60 bg-card/80 backdrop-blur flex-col fixed h-screen z-30"
      >
        <NavContent />
      </aside>

      {/* Mobile Header */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-40 h-14 bg-card/95 backdrop-blur border-b border-border/60 flex items-center px-4">
        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon" className="mr-3">
              <Menu className="w-5 h-5" />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-60 p-0 bg-card border-border/60">
            <NavContent onItemClick={() => setMobileOpen(false)} />
          </SheetContent>
        </Sheet>
        <div className="flex items-center gap-2">
          <Zap className="w-4 h-4 text-primary" />
          <span className="font-semibold text-sm" style={{ fontFamily: 'Space Grotesk' }}>KingTips</span>
        </div>
      </div>

      {/* Main Content */}
      <main className="main-content md:ml-60">
        <div className="pt-14 md:pt-0">
          {children}
        </div>
      </main>
    </div>
  );
}
